<template>
  <div v-if="operadores && operadores.length" :class="['asesor-filter-field', { 'asesor-filter-field--compact': compact }]">
    <label v-if="!compact" class="field-label block mb-1">Asesor</label>
    <div v-if="!compact" class="asesor-input-wrap">
      <i class="pi pi-filter asesor-input-icon"></i>
      <Dropdown
        v-model="selectedId"
        :options="opciones"
        optionLabel="name"
        optionValue="id"
        placeholder="Todos los asesores"
        :showClear="false"
        class="asesor-dropdown"
      />
    </div>
    <div v-else class="asesor-pill-wrap">
      <Dropdown
        v-model="selectedId"
        :options="opciones"
        optionLabel="name"
        optionValue="id"
        :showClear="false"
        :class="['asesor-pill-dropdown', { 'asesor-pill-dropdown--active': opcionSeleccionada }]"
      >
        <template #value>
          <span class="asesor-pill-label">
            <i class="pi pi-filter asesor-pill-icon"></i>
            <span class="asesor-pill-text">{{ opcionSeleccionada ? primerNombre(opcionSeleccionada.name) : 'Global' }}</span>
            <i class="pi pi-chevron-down asesor-pill-arrow"></i>
          </span>
        </template>
      </Dropdown>
    </div>
  </div>
</template>

<script>
import { useGlobalFilter } from '@/stores/globalFilterStore';

const gf = useGlobalFilter();

export default {
  name: 'AsesorFilter',

  props: {
    operadores: {
      type: Array,
      default: () => [],
    },
    compact: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['change'],

  data() {
    return {
      selectedId: gf.get('globalAsesorId'),
      syncingFromStore: false,
    };
  },

  computed: {
    opciones() {
      return [
        { id: null, name: 'Todos los asesores' },
        ...(this.operadores || []),
      ];
    },
    opcionSeleccionada() {
      if (this.selectedId == null) return null;
      return this.opciones.find(o => Number(o.id) === Number(this.selectedId)) || null;
    },
  },

  methods: {
    primerNombre(fullName) {
      if (!fullName) return '';
      return fullName.split(' ')[0];
    },
    /**
     * Sincroniza selectedId cuando otro componente (ej. chip en KPI)
     * modifica el filtro en el store externamente.
     */
    _syncFromStore(storeId) {
      this.syncingFromStore = true;
      this.selectedId = storeId;
      this.$nextTick(() => {
        this.syncingFromStore = false;
      });
    },
  },

  watch: {
    selectedId(id) {
      // Si es una sincronización externa (no viene del dropdown), no propagar al store
      if (this.syncingFromStore) return;

      if (id) {
        const op = (this.operadores || []).find(o => Number(o.id) === Number(id));
        gf.setBatch({
          globalAsesorId: id,
          globalAsesorName: op?.name || '',
        });
      } else {
        gf.clearBatch(['globalAsesorId', 'globalAsesorName']);
      }
      this.$emit('change', id);
    },
  },

  mounted() {
    this._unsubFilter = gf.onChange(() => {
      const storeId = gf.get('globalAsesorId');
      if (Number(storeId) !== Number(this.selectedId)) {
        this._syncFromStore(storeId);
      }
    });
  },

  beforeUnmount() {
    if (this._unsubFilter) this._unsubFilter();
  },
};
</script>

<style>
.asesor-filter-field {
  flex-shrink: 0;
}

.asesor-filter-field--compact {
  flex-shrink: 0;
}

/* ── Compact pill style ── */
.asesor-pill-wrap {
  display: flex;
  align-items: center;
}

.asesor-pill-dropdown.p-dropdown {
  display: inline-flex;
  align-items: center;
  border: 1px solid rgba(148, 163, 184, 0.45);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.94);
  cursor: pointer;
  outline: none;
  transition: border-color 0.18s ease, background-color 0.18s ease, box-shadow 0.18s ease;
  height: 34px;
  padding: 0;
}

.asesor-pill-dropdown.p-dropdown:hover {
  border-color: rgba(99, 102, 241, 0.5);
  background: rgba(239, 246, 255, 0.95);
}

/* ── Active state (asesor específico seleccionado) ── */
.asesor-pill-dropdown--active.p-dropdown {
  border-color: rgba(99, 102, 241, 0.6);
  background: rgba(239, 246, 255, 0.92);
  box-shadow: inset 0 0 0 1px rgba(99, 102, 241, 0.08);
}

.asesor-pill-dropdown--active.p-dropdown:hover {
  border-color: rgba(99, 102, 241, 0.7);
  background: rgba(238, 242, 255, 0.95);
}

.dark .asesor-pill-dropdown--active.p-dropdown {
  border-color: rgba(129, 140, 248, 0.45);
  background: rgba(30, 41, 59, 0.85);
  box-shadow: inset 0 0 0 1px rgba(129, 140, 248, 0.12);
}

.dark .asesor-pill-dropdown--active.p-dropdown:hover {
  border-color: rgba(129, 140, 248, 0.6);
  background: rgba(30, 41, 59, 0.9);
}

.asesor-pill-dropdown--active .asesor-pill-icon {
  color: #4f46e5;
  opacity: 1;
}

.dark .asesor-pill-dropdown--active .asesor-pill-icon {
  color: #a5b4fc;
  opacity: 1;
}

.asesor-pill-dropdown .p-dropdown-label {
  padding: 0 !important;
  display: flex;
  align-items: center;
  border: none !important;
  background: transparent !important;
  outline: none !important;
}

.asesor-pill-dropdown .p-dropdown-trigger {
  display: none !important;
}

.asesor-pill-dropdown .p-dropdown-panel {
  margin-top: 4px;
  border-radius: 10px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  border: 1px solid rgba(148, 163, 184, 0.3);
}

.asesor-pill-dropdown .p-dropdown-items-wrapper {
  max-height: 280px;
}

.asesor-pill-dropdown .p-dropdown-item {
  padding: 8px 12px;
  font-size: 0.82rem;
  transition: background 0.12s ease;
}

.asesor-pill-dropdown .p-dropdown-item:hover {
  background: rgba(99, 102, 241, 0.06);
}

.asesor-pill-dropdown .p-dropdown-item.p-highlight {
  background: rgba(99, 102, 241, 0.12);
  color: #4f46e5;
  font-weight: 600;
}

.asesor-pill-label {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px 4px 8px;
  font-size: 0.76rem;
  font-weight: 600;
  color: #475569;
  white-space: nowrap;
  letter-spacing: 0.01em;
}

.asesor-pill-icon {
  font-size: 0.68rem;
  color: #6366f1;
  opacity: 0.8;
}

.asesor-pill-text {
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.2;
}

.asesor-pill-arrow {
  font-size: 0.55rem;
  color: #94a3b8;
  margin-left: 1px;
  transition: transform 0.18s ease;
}

/* Dark mode */
.dark .asesor-pill-dropdown.p-dropdown {
  border-color: rgba(113, 113, 122, 0.48);
  background: rgba(24, 24, 27, 0.75);
}

.dark .asesor-pill-dropdown.p-dropdown:hover {
  border-color: rgba(129, 140, 248, 0.45);
  background: rgba(30, 41, 59, 0.85);
}

.dark .asesor-pill-label {
  color: #d4d4d8;
}

.dark .asesor-pill-icon {
  color: #a5b4fc;
}

.dark .asesor-pill-arrow {
  color: #71717a;
}

.dark .asesor-pill-dropdown .p-dropdown-panel {
  background: #18181b;
  border-color: rgba(113, 113, 122, 0.4);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
}

.dark .asesor-pill-dropdown .p-dropdown-item {
  color: #d4d4d8;
}

.dark .asesor-pill-dropdown .p-dropdown-item:hover {
  background: rgba(129, 140, 248, 0.08);
}

.dark .asesor-pill-dropdown .p-dropdown-item.p-highlight {
  background: rgba(129, 140, 248, 0.15);
  color: #a5b4fc;
}

.asesor-input-wrap {
  position: relative;
}

.asesor-input-icon {
  position: absolute;
  left: 11px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.72rem;
  color: #94a3b8;
  pointer-events: none;
  z-index: 1;
}

.asesor-dropdown {
  min-width: 175px;
}

.asesor-dropdown .p-dropdown {
  height: 42px;
  border-radius: 10px;
  border: 1px solid rgba(148, 163, 184, 0.55);
  background: rgba(255, 255, 255, 0.96);
  font-size: 0.85rem;
  outline: none;
  transition: border-color 0.15s ease;
}

.asesor-dropdown .p-dropdown:not(.p-disabled):hover {
  border-color: rgba(99, 102, 241, 0.5);
}

.asesor-dropdown .p-dropdown-label {
  padding: 0 30px 0 2rem;
  display: flex;
  align-items: center;
  outline: none;
}

.asesor-dropdown .p-dropdown-trigger {
  width: 28px;
}

.asesor-dropdown .p-dropdown-panel {
  border-radius: 10px;
  margin-top: 4px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  border: 1px solid rgba(148, 163, 184, 0.3);
}

.asesor-dropdown .p-dropdown-items-wrapper {
  max-height: 280px;
}

.asesor-dropdown .p-dropdown-item {
  padding: 9px 12px;
  font-size: 0.83rem;
  transition: background 0.12s ease;
}

.asesor-dropdown .p-dropdown-item:hover {
  background: rgba(99, 102, 241, 0.06);
}

.asesor-dropdown .p-dropdown-item.p-highlight {
  background: rgba(99, 102, 241, 0.12);
  color: #4f46e5;
  font-weight: 600;
}

/* Dark mode */
.dark .asesor-dropdown .p-dropdown {
  border-color: rgba(113, 113, 122, 0.48);
  background: rgba(24, 24, 27, 0.75);
  color: #e4e4e7;
}

.dark .asesor-dropdown .p-dropdown:not(.p-disabled):hover {
  border-color: rgba(129, 140, 248, 0.5);
}

.dark .asesor-dropdown .p-dropdown-panel {
  background: #18181b;
  border-color: rgba(113, 113, 122, 0.4);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
}

.dark .asesor-dropdown .p-dropdown-item {
  color: #d4d4d8;
}

.dark .asesor-dropdown .p-dropdown-item:hover {
  background: rgba(129, 140, 248, 0.08);
}

.dark .asesor-dropdown .p-dropdown-item.p-highlight {
  background: rgba(129, 140, 248, 0.15);
  color: #a5b4fc;
}
</style>
